import { useEffect } from 'react'
import { Control, useFormState, useWatch } from 'react-hook-form'
import { checkTrueRecursively, logError } from 'src/Libs/flow/utils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { Htc320hIo, InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useInstallationForm, useInstallationIsGerman } from 'src/Libs/flow/installation/hooks'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { getTreatHtcAsNonSgReady } from 'src/Libs/flow/installation/utils'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useDevicePairing } from '../../hooks'
import { useFetchSmartMeterGateway } from '../../overview'
import { SmartMeterGatewayDto } from 'src/Libs/flow/installation/api'

interface ReturnType {
  control: Control<InstallationFormModel, unknown>
  htc320h: Htc320hIo | null
  displayName: string
  isDirty: boolean
  isError: boolean
  showHtc320hIoInputs: boolean
  smartMeterGatewayInfo: SmartMeterGatewayDto | undefined
  treatHtcAsNonSgReady: boolean
  handleDelete: () => void
  handlePairing: () => void
}

export const useHtc320hIo = (): ReturnType => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  const { control, getValues, setValue, reset, trigger } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({ control, name: 'ioInterfaces.htc320h' })

  const isDirty = checkTrueRecursively(dirtyFields.ioInterfaces?.htc320h)
  const isError = !!Object.keys(errors.ioInterfaces?.htc320h ?? {}).length

  const htc320h = useWatch({
    control,
    name: 'ioInterfaces.htc320h',
  })

  const { name, isConsumptionActive, deviceId } = htc320h ?? {}

  useEffect(() => {
    trigger('ioInterfaces.htc320h')
  }, [isConsumptionActive, trigger])

  const installation = useWatch({ control })
  const { generalSettings, heatpump, ioInterfaces } = installation as InstallationFormModel
  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, ioInterfaces.storage, heatpump)

  const showHtc320hIoInputs = useInstallationIsGerman()

  const { handlePairing } = useDevicePairing(htc320h)

  const [installationId, emcId] = useWatch({ control, name: ['id', 'emcDevice.deviceId'] })
  const { data: smartMeterGatewayInfo } = useFetchSmartMeterGateway(installationId ?? undefined, emcId)

  const displayName = getDeviceDefaultName(name ?? '', 'HTC320H I/O', deviceId ?? '', t)

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()

  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        const device = getValues('ioInterfaces.htc320h')
        if (!device) {
          logError('HTC320H I/O was not found and could not be deleted.')
          return
        }

        Promise.resolve().then(() => {
          deleteIndividualDevice(device, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }

  return {
    control,
    displayName,
    isDirty,
    isError,
    showHtc320hIoInputs,
    smartMeterGatewayInfo,
    treatHtcAsNonSgReady,
    handleDelete,
    handlePairing,
    htc320h,
  }
}
